/**
 * IssueMIRFromMap — compact MIR-creation dialog launched from the Site
 * Map material drilldown. Pre-fills warehouse + MCI from the clicked
 * material sub-cell, then asks the user to pick a Package Scope via 3
 * tabs (Battery-Limit, Macro-Schedule WBS, 3D-Model branch) matching
 * the same picker style used in Interface & Battery-Limit Management.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { X, ClipboardList, Loader2, Package, Layers, Calendar, Box, Search,
  ChevronRight, ChevronDown, CornerDownRight, Check } from 'lucide-react';
import QRScannerModal from '@/components/common/QRScannerModal';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444' };

export default function IssueMIRFromMap({ projectId, warehouseLocation, material, onClose, onDone }) {
  const isStructural = String(material?.discipline || '').toLowerCase().startsWith('structural');
  const [tab, setTab] = useState(isStructural ? 'MARK' : 'BL');   // 'MARK' | 'BL' | 'WBS' | '3D'
  const [qty, setQty] = useState('');
  const [reqBy, setReqBy] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const [bls, setBls] = useState([]);
  const [wbs, setWbs] = useState([]);
  const [tree3d, setTree3d] = useState([]);
  const [q, setQ] = useState('');
  const [selectedScope, setSelectedScope] = useState(null); // {kind, id, label, wbsActivityId?, workfront?}
  // NEW — mandatory destination site-station + rack
  const [hierarchy, setHierarchy] = useState({ stations: [] });
  const [destStation, setDestStation] = useState('');
  const [destRack, setDestRack] = useState('');

  // ── Structural reservations → tag/mark picker with allocated qty ──
  const [resTags, setResTags] = useState([]);         // [{ tag, priority, marks:[{markNumber, qty}] }]
  const [markSelections, setMarkSelections] = useState({}); // key `${tag}|${mark}` → {tag, markNumber, allocatedQty, issueQty}

  // ── 3D Object Drilldown state (multi-select) ─────────────────────
  const [fourDProjectId, setFourDProjectId] = useState(projectId);
  const [activeModel, setActiveModel] = useState(null); // full model doc
  const [modelRoots, setModelRoots] = useState([]);
  const [childrenMap, setChildrenMap] = useState({}); // dbId -> [nodes]
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [loadingChildren, setLoadingChildren] = useState(() => new Set());
  const [treeSearch, setTreeSearch] = useState('');
  const [treeSearchResults, setTreeSearchResults] = useState(null); // null = tree mode
  // selection map: key `${modelId}:${dbId}` → {modelId, dbId, name, depth, childCount, includeChildren}
  const [selections, setSelections] = useState({});

  useEffect(() => {
    (async () => {
      // Resolve linked resource IDs first — the scheduling module and
      // 4D module live in separate project namespaces so we cannot
      // reuse the core project ID directly.
      let scheduleProjectId = null;
      let fourDProjectIdLocal = projectId;
      try {
        const { data: link } = await api.get(`/v1/projects/${projectId}/mtx/linked-resources`);
        scheduleProjectId = link?.scheduleProjectId || null;
        fourDProjectIdLocal = link?.fourDProjectId || projectId;
      } catch { /* fall back to defaults */ }
      setFourDProjectId(fourDProjectIdLocal);

      // Promise.allSettled so a failure on one endpoint doesn't blank the others.
      const results = await Promise.allSettled([
        api.get(`/v1/projects/${projectId}/battery-limits`),
        scheduleProjectId
          ? api.get(`/scheduling/projects/${scheduleProjectId}/tasks`)
          : Promise.reject(new Error('no-schedule-linked')),
        api.get(`/v1/projects/${fourDProjectIdLocal}/4d/models`),
      ]);
      const [blR, schedR, modelsR] = results;

      if (blR.status === 'fulfilled') {
        const d = blR.value.data;
        setBls((d?.batteryLimits || d?.items || d || []).slice(0, 200));
      }

      if (schedR.status === 'fulfilled') {
        // /scheduling/projects/{scheduleProjectId}/tasks returns a flat
        // array of tasks each with { uid, wbs_code, name, is_summary,
        // outline_level, parent_uid, start_date, ... }.
        const rows = Array.isArray(schedR.value.data) ? schedR.value.data : (schedR.value.data?.tasks || []);
        const activities = rows.map(t => ({
          id: t.uid || t.id || t._id,
          code: t.wbs_code || t.code || '',
          wbs: t.wbs_code || t.code || '',
          name: t.name || '',
          startDate: t.start_date || t.plannedStart || t.baseline_start || null,
          isSummary: !!(t.is_summary),
          level: t.outline_level ?? 0,
        }));
        setWbs(activities.slice(0, 500));
      }

      if (modelsR.status === 'fulfilled') {
        setTree3d((modelsR.value.data?.models || []).slice(0, 30));
      }

      // Warehouse hierarchy for destination station+rack pickers
      try {
        const { data: h } = await api.get(`/v1/projects/${projectId}/mtx/warehouse-hierarchy`);
        setHierarchy(h);
      } catch { setHierarchy({ stations: [] }); }
    })();
  }, [projectId]);

  const max = material?.finalInventory ?? material?.iqcAccepted ?? 0;

  // ── 3D object tree helpers ────────────────────────────────────────
  const openModel = useCallback(async (model) => {
    setActiveModel(model);
    setModelRoots([]); setChildrenMap({}); setExpandedIds(new Set());
    setTreeSearch(''); setTreeSearchResults(null);
    try {
      // Root nodes = depth 0
      const modelId = model.id || model.modelId || model._id;
      const { data } = await api.get(`/v1/projects/${fourDProjectId}/4d/models/${modelId}/object-tree?max_depth=0&limit=500`);
      setModelRoots(data?.objects || []);
    } catch (e) {
      toast.error('Failed to load object tree — has the model finished translating?');
    }
  }, [fourDProjectId]);

  const loadChildren = useCallback(async (node) => {
    if (!activeModel) return;
    const key = String(node.dbId);
    if (childrenMap[key]) return; // cached
    setLoadingChildren(prev => new Set(prev).add(key));
    try {
      const modelId = activeModel.id || activeModel.modelId || activeModel._id;
      const { data } = await api.get(
        `/v1/projects/${fourDProjectId}/4d/models/${modelId}/object-tree?parent_db_id=${node.dbId}&limit=500`
      );
      setChildrenMap(prev => ({ ...prev, [key]: data?.objects || [] }));
    } finally {
      setLoadingChildren(prev => { const s = new Set(prev); s.delete(key); return s; });
    }
  }, [activeModel, childrenMap, fourDProjectId]);

  const toggleExpand = useCallback((node) => {
    const key = String(node.dbId);
    setExpandedIds(prev => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else { s.add(key); if (!childrenMap[key] && node.childCount > 0) loadChildren(node); }
      return s;
    });
  }, [childrenMap, loadChildren]);

  const selKey = (modelId, dbId) => `${modelId}:${dbId}`;
  const toggleSelection = useCallback((node, includeChildren) => {
    if (!activeModel) return;
    const modelId = activeModel.id || activeModel.modelId || activeModel._id;
    const key = selKey(modelId, node.dbId);
    setSelections(prev => {
      const next = { ...prev };
      const existing = next[key];
      if (existing && existing.includeChildren === includeChildren) {
        delete next[key];
      } else {
        next[key] = {
          modelId,
          modelName: activeModel.filename || activeModel.modelName || activeModel.name,
          dbId: node.dbId,
          name: node.name,
          depth: node.depth,
          childCount: node.childCount || 0,
          includeChildren: !!includeChildren,
        };
      }
      return next;
    });
  }, [activeModel]);

  // Structural reservations loader — driven by material.materialCode +
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleScanResult = async (raw) => {
    setScannerOpen(false);
    if (!raw) return;
    try {
      const { data } = await api.get(
        `/v1/projects/${projectId}/swm/scan`, { params: { token: raw.trim() } }
      );
      const preset = data.mirPreset;
      const meta = data.mark;
      let matched = false;
      for (const t of resTags) {
        const hit = (t.marks || []).find((mm) => mm.markNumber === preset.sourceMark);
        if (hit) { toggleMarkSelection(t, hit); matched = true; break; }
      }
      if (matched) toast.success(`Added ${preset.sourceMark} · ${preset.description}`);
      else toast.warning(`Mark ${preset.sourceMark} scanned but not reserved at ${warehouseLocation}. Weight: ${meta.weightKg} kg`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    }
  };

  // Structural reservations loader — driven by material.materialCode +
  // warehouseLocation.  Uses the same matchKeys the warehouse-inventory
  // drilldown uses so partial code aliases match too.
  useEffect(() => {
    if (!isStructural || !projectId) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/swm/allocation/reservations?bin_=${encodeURIComponent(warehouseLocation || '')}`);
        const cand = [
          material?.materialCode,
          (material?.description || '').slice(0, 30).trim(),
          (material?.description || '').slice(0, 20).trim(),
          (material?.materialCode || '').split(' ').slice(0, 3).join(' '),
          (material?.materialCode || '').split(' ').slice(0, 2).join(' '),
        ];
        let hit = null;
        for (const r of (data.reservations || [])) {
          const keys = (r.matchKeys && r.matchKeys.length) ? r.matchKeys : [r.materialCode];
          if (keys.some(k => cand.includes(k))) { hit = r; break; }
        }
        setResTags(hit?.byTag || []);
      } catch {
        setResTags([]);
      }
    })();
  }, [projectId, warehouseLocation, material, isStructural]);

  const toggleMarkSelection = (t, m) => {
    const key = `${t.tag}|${m.markNumber}`;
    setMarkSelections(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = {
        tag: t.tag, priority: t.priority, subPriority: t.subPriority,
        markNumber: m.markNumber, allocatedQty: m.qty,
        issueQty: m.qty,   // auto-fill with allocated qty; user can edit
      };
      return next;
    });
  };
  const updateMarkQty = (key, v) => setMarkSelections(prev => {
    if (!prev[key]) return prev;
    const clamped = Math.max(0, Math.min(Number(v || 0), prev[key].allocatedQty));
    return { ...prev, [key]: { ...prev[key], issueQty: clamped } };
  });

  // Sync total qty + selectedScope when structural marks are picked
  useEffect(() => {
    if (tab !== 'MARK') return;
    const list = Object.values(markSelections);
    if (list.length === 0) {
      if (selectedScope?.kind === 'STRUCT-MARKS') setSelectedScope(null);
      return;
    }
    const total = list.reduce((s, r) => s + Number(r.issueQty || 0), 0);
    setQty(String(total));
    const firstTag = list[0].tag;
    const allSameTag = list.every(r => r.tag === firstTag);
    setSelectedScope({
      kind: 'STRUCT-MARKS',
      id: firstTag,
      label: allSameTag
        ? `${firstTag} · ${list.length} mark${list.length === 1 ? '' : 's'}`
        : `${list.length} marks across ${new Set(list.map(r => r.tag)).size} tags`,
      workfront: `STR:${firstTag}#${list.length}marks`,
      markAllocations: list.map(r => ({
        structuralTag: r.tag, markNumber: r.markNumber,
        allocatedQty: r.allocatedQty, issueQty: r.issueQty,
      })),
    });
  }, [markSelections, tab]); // eslint-disable-line

  // Debounced tree search
  useEffect(() => {
    if (!activeModel || tab !== '3D') return;
    const t = setTimeout(async () => {
      if (!treeSearch.trim()) { setTreeSearchResults(null); return; }
      try {
        const modelId = activeModel.id || activeModel.modelId || activeModel._id;
        const { data } = await api.get(
          `/v1/projects/${fourDProjectId}/4d/models/${modelId}/object-tree?search=${encodeURIComponent(treeSearch)}&limit=300`
        );
        setTreeSearchResults(data?.objects || []);
      } catch { setTreeSearchResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [treeSearch, activeModel, tab, fourDProjectId]);

  // When 3D tab active with selections, sync selectedScope so submit-btn enables
  useEffect(() => {
    if (tab !== '3D') return;
    const list = Object.values(selections);
    if (list.length === 0) { if (selectedScope?.kind === '3D-OBJECTS') setSelectedScope(null); return; }
    const first = list[0];
    setSelectedScope({
      kind: '3D-OBJECTS',
      id: first.modelId,
      label: list.length === 1
        ? `${first.modelName} · ${first.name}${first.includeChildren ? ' (+children)' : ''}`
        : `${first.modelName} · ${list.length} objects pinned`,
      apsModelId: first.modelId,
      apsObjectSelections: list.map(s => ({
        dbId: s.dbId, name: s.name, includeChildren: s.includeChildren,
        depth: s.depth, childCount: s.childCount,
      })),
      workfront: `3D:${first.modelName}#${list.length}obj`,
    });
  }, [selections, tab]); // eslint-disable-line

  const submit = async () => {
    if (!qty || parseFloat(qty) <= 0) return toast.error('Enter a quantity');
    if (parseFloat(qty) > max && !window.confirm(`Qty ${qty} exceeds available (${max}). Proceed anyway?`)) return;
    if (!selectedScope) return toast.error('Pick a package scope (BL / WBS / 3D)');
    if (!destStation || !destRack) return toast.error('Select a destination site station and rack');
    setBusy(true);
    try {
      const scopeNote = `[${selectedScope.kind}] ${selectedScope.label}${notes ? ' · ' + notes : ''}`;
      const { data } = await api.post(`/v1/projects/${projectId}/mtx/mirs`, {
        warehouseLocation,
        wbsActivityId: selectedScope.wbsActivityId || null,
        workfront: selectedScope.workfront || null,
        requiredByDate: reqBy || null,
        notes: scopeNote,
        destinationStationLocation: destStation,
        destinationRackLocation: destRack,
        lineItems: [{ mciLineNumber: material.mciLineNumber, qty: parseFloat(qty) }],
        // NEW · 4D model drilldown
        apsModelId: selectedScope.apsModelId || null,
        apsObjectSelections: selectedScope.apsObjectSelections || [],
        // NEW · Structural per-mark allocations (only present when scope kind = STRUCT-MARKS)
        markAllocations: selectedScope.markAllocations || null,
      });
      toast.success(`MIR ${data.code} created`);
      onDone?.(data); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'MIR failed'); }
    finally { setBusy(false); }
  };

  const filterQ = (text) => !q || (text || '').toLowerCase().includes(q.toLowerCase());

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.55)' }} onClick={onClose}>
      <div className="w-[720px] max-h-[85vh] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()} data-testid="issue-mir-map-dialog">
        <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: C.border, background: '#F8FAFC' }}>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" style={{ color: C.accent }} />
            <div>
              <div className="text-sm font-bold" style={{ color: C.slate }}>Issue MIR · {material?.materialCode}</div>
              <div className="text-[10px]" style={{ color: C.slateMuted }}>
                {material?.description} · MCI {material?.mciLineNumber} · Available <b style={{ color: C.success }}>{max} {material?.unit}</b> at <b>{warehouseLocation}</b>
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="issue-mir-map-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-3 gap-2 p-3 border-b" style={{ borderColor: C.border }}>
          <div>
            <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Qty to issue</label>
            <input value={qty} onChange={e => setQty(e.target.value)} type="number" min="0" max={max}
              data-testid="issue-mir-qty"
              className="w-full px-2 py-1 rounded border text-[11px] font-mono"
              style={{ borderColor: C.border }} />
          </div>
          <div>
            <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Required by</label>
            <input value={reqBy} onChange={e => setReqBy(e.target.value)} type="date"
              className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }} />
          </div>
          <div>
            <label className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Optional context"
              className="w-full px-2 py-1 rounded border text-[11px]" style={{ borderColor: C.border }} />
          </div>
        </div>

        {/* NEW · Destination Site Station + Rack (mandatory) */}
        <div className="grid grid-cols-2 gap-2 p-3 border-b" style={{ borderColor: C.border, background: '#F0FDFA' }}>
          <div>
            <label className="text-[9px] uppercase font-semibold flex items-center gap-1" style={{ color: '#0F766E' }}>
              <Package className="w-3 h-3" /> Destination Site Station *
            </label>
            <select value={destStation}
              onChange={e => { setDestStation(e.target.value); setDestRack(''); }}
              className="w-full px-2 py-1 rounded border text-[11px]"
              style={{ borderColor: C.border, background: '#fff' }}
              data-testid="issue-mir-dest-station">
              <option value="">— pick station —</option>
              {(hierarchy?.stations || []).map(s => (
                <option key={s.id} value={s.location}>{s.label || s.location}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase font-semibold flex items-center gap-1" style={{ color: '#0F766E' }}>
              <Package className="w-3 h-3" /> Destination Rack *
            </label>
            <select value={destRack}
              onChange={e => setDestRack(e.target.value)}
              disabled={!destStation}
              className="w-full px-2 py-1 rounded border text-[11px]"
              style={{ borderColor: C.border, background: destStation ? '#fff' : '#F1F5F9' }}
              data-testid="issue-mir-dest-rack">
              <option value="">— pick rack —</option>
              {(hierarchy?.stations || []).find(s => s.location === destStation)?.racks.map(r => (
                <option key={r.id} value={r.location}>{r.label || r.location}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-3 pt-2 flex items-center gap-1 border-b" style={{ borderColor: C.border }}>
            {[
              ...(isStructural ? [{ k: 'MARK', label: 'Tag → Mark #', icon: CornerDownRight }] : []),
              { k: 'BL',  label: 'Battery-Limit', icon: Layers },
              { k: 'WBS', label: 'Macro Schedule · WBS', icon: Calendar },
              { k: '3D',  label: '3D Model Hierarchy',   icon: Box },
            ].map(t => (
              <button key={t.k} onClick={() => { setTab(t.k); if (t.k !== '3D' && t.k !== 'MARK') setSelectedScope(null); setQ(''); }}
                data-testid={`issue-mir-tab-${t.k.toLowerCase()}`}
                className="px-2 py-1 text-[10.5px] font-semibold flex items-center gap-1"
                style={{ background: tab === t.k ? C.primary : 'transparent',
                          color: tab === t.k ? '#fff' : C.slateMuted,
                          borderRadius: '4px 4px 0 0' }}>
                <t.icon className="w-3 h-3" /> {t.label}
              </button>
            ))}
          </div>
          <div className="p-3">
            {tab === 'MARK' && (
              <div data-testid="issue-mir-mark-picker">
                {resTags.length === 0 ? (
                  <div className="p-4 text-[11px] rounded" style={{ background: '#FEF3C7', color: '#92400E' }}>
                    No hard allocations found for this material at <b>{warehouseLocation}</b>.
                    Hard-allocate this stock first (SWM → Allocation Engine → Preview Hard) so it can be issued to a specific mark.
                  </div>
                ) : (
                  <div className="rounded border overflow-hidden" style={{ borderColor: C.border }}>
                    <div className="px-2 py-1.5 flex items-center gap-2" style={{ background: '#FAF5FF' }}>
                      <span className="text-[10px] uppercase font-bold" style={{ color: '#7C3AED' }}>
                        🔒 {resTags.length} tag{resTags.length > 1 ? 's' : ''} · pick marks to issue
                      </span>
                      <div className="relative flex-1 max-w-[240px]">
                        <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2" style={{ color: '#7C3AED' }} />
                        <input value={q} onChange={e => setQ(e.target.value)}
                          placeholder="Search tag or mark #…"
                          className="w-full pl-6 pr-2 py-1 rounded border text-[10.5px]"
                          style={{ borderColor: '#E9D5FF', background: '#fff' }}
                          data-testid="issue-mir-mark-search" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setScannerOpen(true)}
                        className="px-2 py-1 rounded text-[10px] font-semibold inline-flex items-center gap-1"
                        style={{ background: '#F59E0B20', color: '#B45309', border: '1px solid #FDE68A' }}
                        data-testid="issue-mir-scan-qr">
                        📷 Scan QR
                      </button>
                      <span className="ml-auto text-[10px]" style={{ color: '#64748B' }}>
                        {Object.keys(markSelections).length} selected · total <b style={{ color: C.success }}>{Object.values(markSelections).reduce((s, r) => s + Number(r.issueQty || 0), 0)} {material?.unit}</b>
                      </span>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      <table className="w-full text-[10.5px]">
                        <thead className="sticky top-0" style={{ background: '#F3E8FF', color: '#6B21A8' }}>
                          <tr>
                            <th className="p-1 w-5">
                              {(() => {
                                const filteredRows = resTags.flatMap(t => (t.marks || []).map(m => ({ t, m })))
                                  .filter(({ t, m }) => {
                                    if (!q) return true;
                                    const s = q.toLowerCase();
                                    return (t.tag || '').toLowerCase().includes(s) || (m.markNumber || '').toLowerCase().includes(s);
                                  });
                                const allSelected = filteredRows.length > 0 && filteredRows.every(({ t, m }) => markSelections[`${t.tag}|${m.markNumber}`]);
                                return <input type="checkbox" checked={allSelected} readOnly
                                  onClick={() => {
                                    setMarkSelections(prev => {
                                      const next = { ...prev };
                                      if (allSelected) { filteredRows.forEach(({ t, m }) => delete next[`${t.tag}|${m.markNumber}`]); }
                                      else { filteredRows.forEach(({ t, m }) => {
                                        const key = `${t.tag}|${m.markNumber}`;
                                        if (!next[key]) next[key] = { tag: t.tag, priority: t.priority, subPriority: t.subPriority, markNumber: m.markNumber, allocatedQty: m.qty, issueQty: m.qty };
                                      }); }
                                      return next;
                                    });
                                  }}
                                  data-testid="issue-mir-mark-selectall" />;
                              })()}
                            </th>
                            <th className="p-1 text-left uppercase text-[8.5px]">Tag / Mark #</th>
                            <th className="p-1 text-left uppercase text-[8.5px]">Priority</th>
                            <th className="p-1 text-right uppercase text-[8.5px]">Allocated</th>
                            <th className="p-1 text-right uppercase text-[8.5px]">Issue Qty (edit)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resTags.flatMap(t => (t.marks || []).map(m => ({ t, m })))
                            .filter(({ t, m }) => {
                              if (!q) return true;
                              const s = q.toLowerCase();
                              return (t.tag || '').toLowerCase().includes(s) || (m.markNumber || '').toLowerCase().includes(s);
                            })
                            .map(({ t, m }) => {
                            const key = `${t.tag}|${m.markNumber}`;
                            const sel = markSelections[key];
                            return (
                              <tr key={key} style={{ borderTop: '1px solid #F3E8FF', background: sel ? '#EFF6FF' : '#fff' }}
                                data-testid={`issue-mir-mark-${m.markNumber}`}>
                                <td className="p-1 text-center">
                                  <input type="checkbox" checked={!!sel} onChange={() => toggleMarkSelection(t, m)} />
                                </td>
                                <td className="p-1 font-mono" style={{ color: C.slate }}>
                                  <div className="font-semibold" style={{ color: '#7C3AED' }}>{t.tag}</div>
                                  <div className="text-[9.5px]" style={{ color: C.slateMuted }}>{m.markNumber}</div>
                                </td>
                                <td className="p-1">
                                  {t.priority
                                    ? <span className="px-1.5 py-0.5 rounded text-[8.5px] font-bold" style={{
                                        background: t.priority === 1 ? '#EF444420' : t.priority === 2 ? '#F5920620' : t.priority === 3 ? '#EAB30820' : '#A855F720',
                                        color:      t.priority === 1 ? '#EF4444'   : t.priority === 2 ? '#F59206'   : t.priority === 3 ? '#EAB308'   : '#7C3AED' }}>
                                        P{t.priority}·{t.subPriority ?? '—'}
                                      </span>
                                    : <span style={{ color: C.slateMuted }}>—</span>}
                                </td>
                                <td className="p-1 text-right font-mono" style={{ color: C.slateMuted }}>
                                  {Number(m.qty).toLocaleString()} {material?.unit}
                                </td>
                                <td className="p-1 text-right">
                                  {sel
                                    ? <input type="number" min={0} max={m.qty} value={sel.issueQty}
                                        onChange={e => updateMarkQty(key, e.target.value)}
                                        className="w-20 px-1 py-0.5 text-right font-mono border rounded"
                                        style={{ borderColor: C.border }}
                                        data-testid={`issue-mir-qty-${m.markNumber}`} />
                                    : <span style={{ color: C.slateMuted }}>—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            {tab !== 'MARK' && (
              <div className="relative mb-2">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2" style={{ color: C.slateMuted }} />
                <input value={q} onChange={e => setQ(e.target.value)}
                  placeholder={tab === 'BL' ? 'Search BL name / code…' : tab === 'WBS' ? 'Search activity / WBS…' : 'Search 3D branch / model…'}
                  className="pl-7 pr-2 py-1 rounded border text-[11px] w-full"
                  style={{ borderColor: C.border }}
                  data-testid="issue-mir-scope-search" />
              </div>
            )}
            <div className="rounded border max-h-[280px] overflow-y-auto" style={{ borderColor: C.border }}>
              {tab === 'BL' && bls.filter(b => filterQ(`${b.code} ${b.name} ${b.discipline}`)).map(b => (
                <button key={b.id || b.code}
                  onClick={() => setSelectedScope({ kind: 'BL', id: b.id || b.code,
                                                     label: `${b.code} · ${b.name}`,
                                                     workfront: b.code })}
                  data-testid={`issue-mir-scope-bl-${b.code}`}
                  className="w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-slate-50"
                  style={{ borderBottom: `1px solid ${C.border}`,
                            background: selectedScope?.id === (b.id || b.code) ? '#EEF2FF' : '#fff' }}>
                  <span className="font-mono text-[10px] font-bold" style={{ color: C.primary }}>{b.code}</span>
                  <span className="text-[10.5px]" style={{ color: C.slate }}>{b.name}</span>
                  <span className="ml-auto text-[9px]" style={{ color: C.slateMuted }}>{b.discipline}</span>
                </button>
              ))}
              {tab === 'WBS' && wbs.filter(t => filterQ(`${t.code} ${t.name} ${t.wbs}`)).map(t => (
                <button key={t.id || t.code}
                  onClick={() => setSelectedScope({ kind: 'WBS', id: t.id || t.code,
                                                     label: `${t.wbs || t.code} · ${t.name}`,
                                                     wbsActivityId: t.id || t.code })}
                  data-testid={`issue-mir-scope-wbs-${t.code || t.id}`}
                  className="w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-slate-50"
                  style={{ borderBottom: `1px solid ${C.border}`,
                            background: selectedScope?.id === (t.id || t.code) ? '#EEF2FF' : '#fff',
                            paddingLeft: `${8 + (t.level || 0) * 10}px` }}>
                  <span className="font-mono text-[10px] font-bold" style={{ color: t.isSummary ? C.slateMuted : C.primary }}>{t.wbs || t.code}</span>
                  <span className="text-[10.5px]" style={{ color: C.slate, fontWeight: t.isSummary ? 600 : 400 }}>{t.name}</span>
                  {t.startDate && <span className="ml-auto text-[9px]" style={{ color: C.slateMuted }}>{String(t.startDate).slice(0, 10)}</span>}
                </button>
              ))}
              {tab === '3D' && !activeModel && tree3d.filter(m => filterQ(m.filename || m.name)).map(m => (
                <button key={m.id || m.modelId}
                  onClick={() => openModel(m)}
                  data-testid={`issue-mir-scope-3d-${m.id || m.modelId}`}
                  className="w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-slate-50"
                  style={{ borderBottom: `1px solid ${C.border}`, background: '#fff' }}>
                  <Box className="w-3 h-3" style={{ color: C.primary }} />
                  <span className="text-[10.5px]" style={{ color: C.slate }}>{m.filename || m.name || m.modelName}</span>
                  {(m.leafCount || m.objectCount || m.totalModelObjects) ? (
                    <span className="ml-auto text-[9px]" style={{ color: C.slateMuted }}>{m.leafCount || m.objectCount || m.totalModelObjects} objects</span>
                  ) : (
                    <span className="ml-auto text-[9px] italic" style={{ color: C.slateMuted }}>open tree →</span>
                  )}
                  <ChevronRight className="w-3 h-3" style={{ color: C.slateMuted }} />
                </button>
              ))}
              {tab === '3D' && activeModel && (
                <div className="flex flex-col" data-testid="issue-mir-3d-tree">
                  <div className="flex items-center gap-2 px-2 py-1.5 border-b" style={{ borderColor: C.border, background: '#F1F5F9' }}>
                    <button onClick={() => { setActiveModel(null); setSelections({}); setTreeSearch(''); setTreeSearchResults(null); }}
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded border"
                      style={{ borderColor: C.border, color: C.slate }}
                      data-testid="issue-mir-3d-back">← models</button>
                    <span className="text-[10.5px] font-semibold truncate" style={{ color: C.slate }}>
                      {activeModel.filename || activeModel.modelName || activeModel.name}
                    </span>
                    <span className="ml-auto text-[9px] font-mono" style={{ color: C.slateMuted }}>
                      {Object.keys(selections).length} pinned
                    </span>
                    {Object.keys(selections).length > 0 && (
                      <button onClick={() => setSelections({})}
                        className="text-[9px] underline"
                        style={{ color: C.danger }}
                        data-testid="issue-mir-3d-clear">clear</button>
                    )}
                  </div>
                  <div className="px-2 py-1.5 border-b" style={{ borderColor: C.border }}>
                    <div className="relative">
                      <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2" style={{ color: C.slateMuted }} />
                      <input value={treeSearch} onChange={e => setTreeSearch(e.target.value)}
                        placeholder="Search object name (Pipe-1234, Beam W12, ...)"
                        className="pl-7 pr-2 py-1 rounded border text-[11px] w-full"
                        style={{ borderColor: C.border }}
                        data-testid="issue-mir-3d-search" />
                    </div>
                  </div>
                  <div>
                    {treeSearchResults !== null
                      ? renderSearchResults(treeSearchResults, selections, activeModel, toggleSelection)
                      : renderTree(modelRoots, 0, { childrenMap, expandedIds, loadingChildren, selections, activeModel, toggleExpand, toggleSelection })
                    }
                  </div>
                </div>
              )}
              {tab === 'BL' && bls.length === 0 && (
                <div className="p-3 text-center text-[10.5px] italic" style={{ color: C.slateMuted }}>
                  No battery limits configured — set them up in <a href="/project-controls/interfaces?tab=battery" className="underline" style={{ color: C.primary }}>Interface Management · Battery Limits</a>.
                </div>
              )}
              {tab === 'WBS' && wbs.length === 0 && (
                <div className="p-3 text-center text-[10.5px] italic" style={{ color: C.slateMuted }}>
                  No macro-schedule linked yet — build one in <a href="/scheduling" className="underline" style={{ color: C.primary }}>Scheduling</a>, then attach it via <span className="font-mono text-[9.5px]">PUT /mtx/linked-resources</span>.
                </div>
              )}
              {tab === '3D' && !activeModel && tree3d.length === 0 && (
                <div className="p-3 text-center text-[10.5px] italic" style={{ color: C.slateMuted }}>
                  No 3D model uploaded for this project yet — open <a href="/project-controls/4d-model" className="underline" style={{ color: C.primary }}>4D Modelling</a> to upload one, then re-open this dialog.
                </div>
              )}
            </div>
            {selectedScope && (
              <div className="mt-2 px-2 py-1.5 rounded text-[10px] font-mono" style={{ background: '#EEF2FF', color: C.primary }}>
                Scope: <b>{selectedScope.kind}</b> · {selectedScope.label}
                {selectedScope.apsObjectSelections?.length > 1 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedScope.apsObjectSelections.slice(0, 8).map(s => (
                      <span key={s.dbId} className="px-1.5 py-0.5 rounded bg-white text-[9px]" style={{ border: `1px solid ${C.border}` }}>
                        #{s.dbId} {s.name}{s.includeChildren ? ' +children' : ''}
                      </span>
                    ))}
                    {selectedScope.apsObjectSelections.length > 8 && (
                      <span className="text-[9px]" style={{ color: C.slateMuted }}>
                        +{selectedScope.apsObjectSelections.length - 8} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-3 border-t flex items-center justify-end gap-2" style={{ borderColor: C.border, background: '#F8FAFC' }}>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="issue-mir-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy || !qty || !selectedScope || !destStation || !destRack}
            data-testid="issue-mir-submit"
            style={{ background: C.accent, color: '#fff' }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><ClipboardList className="w-3.5 h-3.5 mr-1" /> Create MIR</>}
          </Button>
        </div>
      </div>
      {scannerOpen && (
        <QRScannerModal onResult={handleScanResult}
                        onClose={() => setScannerOpen(false)}
                        title="Scan Mark QR / Barcode → Issue MIR" />
      )}
    </div>
  );
}

// ── 3D Object Tree renderers (kept out of component for readability) ──────
function TreeRow({ node, indent, ctx }) {
  const key = String(node.dbId);
  const { childrenMap, expandedIds, loadingChildren, selections, activeModel,
          toggleExpand, toggleSelection } = ctx;
  const expanded = expandedIds.has(key);
  const isLoading = loadingChildren.has(key);
  const hasChildren = (node.childCount || 0) > 0;
  const modelId = activeModel.id || activeModel.modelId || activeModel._id;
  const selKey = `${modelId}:${node.dbId}`;
  const sel = selections[selKey];
  const isLeafSelected = sel && !sel.includeChildren;
  const isBranchSelected = sel && sel.includeChildren;

  return (
    <>
      <div className="flex items-center gap-1 hover:bg-slate-50 border-b"
        style={{ paddingLeft: `${8 + indent * 12}px`, paddingRight: 8, paddingTop: 3, paddingBottom: 3,
                 borderColor: 'rgba(15,23,42,0.06)', background: (isLeafSelected || isBranchSelected) ? '#EEF2FF' : 'transparent' }}
        data-testid={`issue-mir-3d-row-${node.dbId}`}>
        <button onClick={() => hasChildren && toggleExpand(node)}
          className="w-4 h-4 flex items-center justify-center rounded hover:bg-slate-200"
          disabled={!hasChildren}
          data-testid={`issue-mir-3d-expand-${node.dbId}`}>
          {isLoading ? <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#64748B' }} />
            : hasChildren ? (expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />)
            : <CornerDownRight className="w-2.5 h-2.5" style={{ color: '#CBD5E1' }} />}
        </button>
        <Box className="w-3 h-3" style={{ color: hasChildren ? '#1E4E8C' : '#64748B' }} />
        <span className="text-[10.5px] truncate flex-1" title={node.name} style={{ color: '#0F172A' }}>
          {node.name || `dbId ${node.dbId}`}
        </span>
        <span className="text-[9px] font-mono" style={{ color: '#64748B' }}>#{node.dbId}</span>
        {hasChildren && (
          <button onClick={() => toggleSelection(node, true)}
            className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1"
            style={{ border: `1px solid ${isBranchSelected ? '#1E4E8C' : 'rgba(15,23,42,0.10)'}`,
                     background: isBranchSelected ? '#1E4E8C' : '#fff',
                     color: isBranchSelected ? '#fff' : '#0F172A' }}
            data-testid={`issue-mir-3d-pick-branch-${node.dbId}`}
            title="Pin this branch and everything below it">
            {isBranchSelected && <Check className="w-2.5 h-2.5" />} +children
          </button>
        )}
        <button onClick={() => toggleSelection(node, false)}
          className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1"
          style={{ border: `1px solid ${isLeafSelected ? '#F26B1F' : 'rgba(15,23,42,0.10)'}`,
                   background: isLeafSelected ? '#F26B1F' : '#fff',
                   color: isLeafSelected ? '#fff' : '#0F172A' }}
          data-testid={`issue-mir-3d-pick-leaf-${node.dbId}`}
          title="Pin this object only (no descendants)">
          {isLeafSelected && <Check className="w-2.5 h-2.5" />} this only
        </button>
      </div>
      {expanded && childrenMap[key] && childrenMap[key].map(child => (
        <TreeRow key={child.dbId} node={child} indent={indent + 1} ctx={ctx} />
      ))}
    </>
  );
}

function renderTree(nodes, indent, ctx) {
  if (!nodes || nodes.length === 0) {
    return (
      <div className="p-3 text-center text-[10.5px] italic" style={{ color: '#64748B' }}>
        Object tree is empty. If the model was just uploaded, wait for SVF2 translation to complete and refresh.
      </div>
    );
  }
  return nodes.map(n => <TreeRow key={n.dbId} node={n} indent={indent} ctx={ctx} />);
}

function renderSearchResults(results, selections, activeModel, toggleSelection) {
  if (!results || results.length === 0) {
    return (
      <div className="p-3 text-center text-[10.5px] italic" style={{ color: '#64748B' }}>
        No objects match. Try a different keyword.
      </div>
    );
  }
  const modelId = activeModel.id || activeModel.modelId || activeModel._id;
  return results.map(n => {
    const sel = selections[`${modelId}:${n.dbId}`];
    const hasChildren = (n.childCount || 0) > 0;
    return (
      <div key={n.dbId} className="flex items-center gap-1 hover:bg-slate-50 border-b"
        style={{ paddingLeft: 8, paddingRight: 8, paddingTop: 3, paddingBottom: 3,
                 borderColor: 'rgba(15,23,42,0.06)', background: sel ? '#EEF2FF' : 'transparent' }}
        data-testid={`issue-mir-3d-searchrow-${n.dbId}`}>
        <Box className="w-3 h-3" style={{ color: '#1E4E8C' }} />
        <span className="text-[10.5px] truncate flex-1" title={n.name} style={{ color: '#0F172A' }}>{n.name || `dbId ${n.dbId}`}</span>
        <span className="text-[9px] font-mono" style={{ color: '#64748B' }}>#{n.dbId} · d{n.depth ?? '?'}</span>
        {hasChildren && (
          <button onClick={() => toggleSelection(n, true)}
            className="text-[9px] px-1.5 py-0.5 rounded"
            style={{ border: `1px solid ${sel?.includeChildren ? '#1E4E8C' : 'rgba(15,23,42,0.10)'}`,
                     background: sel?.includeChildren ? '#1E4E8C' : '#fff',
                     color: sel?.includeChildren ? '#fff' : '#0F172A' }}>
            +children
          </button>
        )}
        <button onClick={() => toggleSelection(n, false)}
          className="text-[9px] px-1.5 py-0.5 rounded"
          style={{ border: `1px solid ${sel && !sel.includeChildren ? '#F26B1F' : 'rgba(15,23,42,0.10)'}`,
                   background: sel && !sel.includeChildren ? '#F26B1F' : '#fff',
                   color: sel && !sel.includeChildren ? '#fff' : '#0F172A' }}>
          this only
        </button>
      </div>
    );
  });
}
